import { useSubmit } from "@certn/api";
import { showToast, Text } from "@certn/cascadia";
import { Container, SectionMessage } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useContext, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { PackageReviewPreview } from "./components/PackageReviewPreview";
import { useUser } from "../../../../../hooks/useUser";
import { PackageContext } from "../../PackageContext";
import {
  getMissingClientInputClaimsMessage,
  packageContextToAPI,
  shouldShowClientInputConfiguration,
} from "../../package-utils";
import { PackageStepLayout } from "../PackageStepLayout";

export function ReviewStep() {
  const { packageData } = useContext(PackageContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { details: userDetails } = useUser();

  const atsValidation = useMemo(() => {
    if (!packageData.is_ats_package && !packageData.is_api_package) {
      return { isValid: true, errors: [] };
    }

    const errors: string[] = [];

    if (shouldShowClientInputConfiguration(packageData)) {
      if (
        !packageData.client_input_claims ||
        Object.keys(packageData.client_input_claims).length === 0
      ) {
        errors.push(getMissingClientInputClaimsMessage(t));
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, [packageData, t]);

  const { submit, isSubmitting } = useSubmit(
    `/api/internal/client-portal/packages/create/`,
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: () => {
        showToast(t("Package created"), { type: "info" });
        navigate("/packages");
      },
    }
  );

  const submitPackage = async () => {
    try {
      await submit(packageContextToAPI(packageData, userDetails));
    } catch (e) {
      console.error("Error", e);
      showToast(t("Something went wrong, try again"), { type: "error" });
    }
  };

  return (
    <PackageStepLayout
      onComplete={submitPackage}
      disableCompletion={
        ((packageData.checks || []).length === 0 &&
          (packageData.bundles || []).length === 0) ||
        !atsValidation.isValid
      }
      submitting={isSubmitting}
    >
      <Container
        padding={["space-0", "space-1"]}
        className={styles.container}
        layout="narrow"
        testID="review-page-outermost-container"
      >
        <Text as="h1" style="global/title-small">
          {t("Review and save")}
        </Text>
        <div className={styles.mainContentContainer}>
          {!atsValidation.isValid && (
            <SectionMessage
              sectionTitle={t("Configuration required")}
              icon="error"
              type="danger"
            >
              {atsValidation.errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </SectionMessage>
          )}
          <SectionMessage
            sectionTitle={t("Editing packages once created")}
            icon="info"
            type="info"
          >
            <Trans>
              Please note that once a package is created, the checks and group
              purpose cannot be changed. Please review all information
              carefully.
            </Trans>
          </SectionMessage>
          <PackageReviewPreview
            packageData={packageData}
            currency={
              userDetails?.customer_groups[0].billing_profile?.currency || "CAD"
            }
          />
        </div>
      </Container>
    </PackageStepLayout>
  );
}

const styles = {
  container: css({
    rowGap: "var(--space-6)",
  }),
  mainContentContainer: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-6)",
  }),
};
